// Екранът „Дневни отчети" — табът „Персонални" (Справки → personal).
//
// Списък на ВЕЧЕ ПУСНАТИТЕ персонални отчети за периода. Генерирането и
// печатът живеят в „Маркиране"; тук се чете и се отваря готовият отчет.
//
// `GET /reports` връща целия период наведнъж, затова споделената таблица
// работи в КЛИЕНТСКИ режим: сортирането, филтрите в колоните и редът „Общо"
// се смятат в браузъра.
//
// Вторият таб („Заведение") е ОТДЕЛЕН URL и се отваря с навигация — виж
// `dailyReports/DailyReportsTabs.tsx`.
//
// Кликът върху ред отваря `PersonalReportPreviewModal` — същият модал, който
// показва отчета в „Маркиране" и се печата по задание. Той НЕ се пипа оттук.

import { useEffect, useMemo, useState } from "react";
import { App, Spin } from "antd";
import { isAxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import { useViewport } from "../../../hooks/useViewport";
import { useAppSelector } from "../../../store/hooks";
import { ReportDatePicker } from "../../../components/ReportDatePicker";
import { useReportsDateParam } from "../useReportsDateParam";
import { DataTable } from "../../../components/dataTable";
import {
  usePersonalReportById,
  usePersonalReports,
} from "../../../hooks/useReports";
import type { Report } from "../../../api/reports";
import { PersonalReportPreviewModal } from "../../ordering/components/tables/reports/PersonalReportPreviewModal";
import ExportExcelButton from "./ExportExcelButton";
import { ReportTopBar } from "./ReportTopBar";
import { previousPeriodOf } from "./reportPeriod";
import { pluralLabel } from "./reportPlural";
import {
  DailyReportsTabs,
  type DailyReportsTab,
} from "./dailyReports/DailyReportsTabs";
import { PersonalReportsKpis } from "./dailyReports/PersonalReportsKpis";
import { PersonalReportMobileCard } from "./dailyReports/PersonalReportMobileCard";
import { usePersonalReportsColumns } from "./dailyReports/personalReportsColumns";
import { exportPersonalReports } from "./dailyReports/personalReportsExport";
import {
  businessDayEndFrom,
  businessDayStartFrom,
} from "../../../lib/businessDay";
import {
  isZeroAmount,
  summarizePersonalReports,
} from "./dailyReports/personalReportsMath";

/** Правото на втория таб — без него бутонът би водил до чужда справка. */
const ESTABLISHMENT_PERMISSION = "view_establishment_reports";

type Props = { onOpenMobileMenu?: () => void; isNarrow?: boolean };

export const PersonalReportsPage = ({
  onOpenMobileMenu,
  isNarrow = false,
}: Props = {}) => {
  const { t } = useTranslation();
  const { isNarrow: viewportNarrow } = useViewport();
  const narrow = isNarrow || viewportNarrow;
  const { message } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = useAppSelector((state) => state.auth.permissions);

  // Датата живее и в `?date=` — пази се при смяна на справка.
  const [dateRange, setDateRange] = useReportsDateParam(() => [
    businessDayStartFrom(dayjs()),
    businessDayEndFrom(dayjs()),
  ], "business");
  // Изключено по подразбиране: справката отговаря първо на въпроса „колко
  // е сега", а Δ-тата са втори въпрос и струват втора заявка за период,
  // който човек не е поискал.
  const [compare, setCompare] = useState(false);
  const [searchText, setSearchText] = useState("");
  // Редовете СЛЕД филтър и подредба — Excel изнася видяното, не суровия набор.
  const [visibleReports, setVisibleReports] = useState<Report[]>([]);
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const filters = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return {};
    return {
      date_from: businessDayStartFrom(dateRange[0]).format("YYYY-MM-DD HH:mm:ss"),
      date_to: businessDayEndFrom(dateRange[1]).format("YYYY-MM-DD HH:mm:ss"),
    };
  }, [dateRange]);

  // Предходният равен по дължина отрязък — само за Δ чиповете. Тече ли още
  // избраният период, краят се подрязва до изтеклото (виж `reportPeriod`).
  const previousFilters = useMemo(() => {
    if (!compare || !dateRange?.[0] || !dateRange?.[1]) return null;
    // СЪЩИТЕ граници, с които се прави заявката (редовете по-горе нормализират
    // деня). Екранът не подава `withTime`, а ReportDatePicker слага
    // `endOf("day")` само тогава — от календара краят идва 00:00 и предходният
    // период излизаше с ден по-къс, тоест Δ показваше ръст, какъвто няма.
    const previous = previousPeriodOf(
      businessDayStartFrom(dateRange[0]),
      businessDayEndFrom(dateRange[1]),
    );
    return {
      date_from: previous.start.format("YYYY-MM-DD HH:mm:ss"),
      date_to: previous.end.format("YYYY-MM-DD HH:mm:ss"),
    };
  }, [compare, dateRange]);

  const {
    data: reportsData,
    isLoading,
    isFetching,
    refetch,
    error: reportsError,
  } = usePersonalReports(filters);

  // При изключено сравнение се пита за СЪЩИЯ период — React Query го дава от
  // кеша по същия ключ, без втора мрежова заявка.
  const { data: previousData } = usePersonalReports(previousFilters ?? filters);

  const reports = useMemo(() => reportsData?.items ?? [], [reportsData]);
  const previousReports = useMemo(
    () => (previousFilters ? (previousData?.items ?? []) : []),
    [previousFilters, previousData],
  );

  const {
    data: selectedReport,
    isFetching: isLoadingDetail,
    error: reportDetailError,
  } = usePersonalReportById(selectedReportId);

  useEffect(() => {
    const hasForbidden = [reportsError, reportDetailError].some(
      (error) => isAxiosError(error) && error.response?.status === 403,
    );
    if (hasForbidden) message.error(t("common.forbidden"));
  }, [reportsError, reportDetailError, message, t]);

  // Детайлът се дърпа по id; модалът се отваря чак когато отчетът е тук —
  // иначе за миг се вижда празен документ.
  useEffect(() => {
    if (selectedReport && selectedReportId) setIsPreviewOpen(true);
  }, [selectedReport, selectedReportId]);

  // Търсачката гледа това, което човек чете в реда: номера и сервитьора.
  const searchedReports = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    if (!needle) return reports;
    return reports.filter((report) =>
      `#${report.report_number} ${report.user_name ?? ""}`
        .toLowerCase()
        .includes(needle),
    );
  }, [reports, searchText]);

  const summary = useMemo(
    () => summarizePersonalReports(reports),
    [reports],
  );
  const previousSummary = useMemo(
    () => (previousFilters ? summarizePersonalReports(previousReports) : null),
    [previousFilters, previousReports],
  );

  // Платено извън в брой и карта („По сметка", персонализирани методи) не бива
  // да изчезва — има ли такова, се показват и колоната, и кутийката „Друго".
  const showOther = !isZeroAmount(summary.other);

  const columns = usePersonalReportsColumns(showOther);

  const canSeeEstablishment = permissions.some(
    (permission) => permission.name === ESTABLISHMENT_PERMISSION,
  );
  const tabs: DailyReportsTab[] = canSeeEstablishment
    ? ["personal", "establishment"]
    : ["personal"];

  const handleTabChange = (tab: DailyReportsTab) => {
    if (tab === "personal") return;
    // `location.search` носи периода — иначе вторият отчет отваря „днес".
    navigate({ pathname: "/reports/establishment", search: location.search });
  };

  const shownCount = visibleReports.length;

  const tableTitle = (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="font-pos-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
        {t("reportsPage.personalReports")}
      </span>
      <span className="text-[16px] font-extrabold leading-none text-ink">
        {pluralLabel(
          t,
          shownCount,
          "reportsPage.personalResultOne",
          "reportsPage.personalResults",
        )}
        <span className="font-medium text-muted">
          {` · ${t("reportsPage.dailyListHint")}`}
        </span>
      </span>
    </div>
  );

  return (
    <div className="flex h-full flex-col bg-canvas">
      <ReportTopBar
        title={t("reportsPage.dailyReportsTitle")}
        isNarrow={narrow}
        onOpenMobileMenu={onOpenMobileMenu}
        onRefresh={() => refetch()}
        isFetching={isFetching}
      />

      <div
        className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${
          narrow ? "px-3.5 pb-6 pt-3.5" : "px-7 pb-8 pt-[22px]"
        }`}
      >
        <div className="flex flex-col gap-3.5">
          <div className="flex flex-wrap items-center justify-center gap-2 overflow-x-auto pb-0.5">
            <ReportDatePicker
              dayMode="business"
              value={dateRange}
              onChange={setDateRange}
              defaultMode="day"
            />
            <button
              type="button"
              onClick={() => setCompare((previous) => !previous)}
              aria-pressed={compare}
              className={`h-9 shrink-0 rounded-lg border px-3.5 text-[12.5px] font-bold transition-colors ${
                compare
                  ? "border-brand-tint2 bg-brand-tint text-brand-deep-ink"
                  : "border-shade-3 bg-surface text-soft hover:bg-fill"
              }`}
            >
              {compare ? "✓ " : ""}
              {t("reportsPage.billsHistoryComparePrevious")}
            </button>
          </div>

          <DailyReportsTabs
            value="personal"
            onChange={handleTabChange}
            tabs={tabs}
            labels={{
              personal: t("reportsPage.dailyTabPersonal"),
              establishment: t("reportsPage.dailyTabEstablishment"),
            }}
          />

          {isLoading ? (
            <div className="grid place-items-center py-20">
              <Spin size="large" />
            </div>
          ) : (
            <>
              <PersonalReportsKpis
                summary={summary}
                previous={previousSummary}
                isNarrow={narrow}
              />

              <DataTable<Report>
                tableId="daily-personal-reports"
                data={searchedReports}
                columns={columns}
                getRowId={(row) => String(row.id)}
                refreshing={isFetching || isLoadingDetail}
                ariaLabel={t("reportsPage.personalReports")}
                title={tableTitle}
                search={{
                  value: searchText,
                  onChange: setSearchText,
                  placeholder: t("reportsPage.dailySearchPlaceholder"),
                }}
                onFilteredRowsChange={setVisibleReports}
                toolbarRight={
                  <ExportExcelButton
                    onExport={() =>
                      exportPersonalReports(visibleReports, { t, showOther })
                    }
                    disabled={shownCount === 0}
                    bare
                  />
                }
                mobileCard={(row) => (
                  <PersonalReportMobileCard report={row} showOther={showOther} />
                )}
                onRowClick={(row) => setSelectedReportId(row.id)}
                rowTone={(row) =>
                  row.id === selectedReportId ? "brand" : null
                }
                initialSorting={[{ id: "report_number", desc: true }]}
                emptyTitle={t("reportsPage.noReportsFound")}
                emptyHint={t("reportsPage.dailyEmptyHint")}
              />
            </>
          )}
        </div>
      </div>

      {/* Детайлният отчет — по задание и се печата, затова се ползва както е. */}
      <PersonalReportPreviewModal
        open={isPreviewOpen}
        onClose={() => {
          setIsPreviewOpen(false);
          setSelectedReportId(null);
        }}
        report={selectedReport ?? null}
      />
    </div>
  );
};
